import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cookie-consent';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  functional: false,
};

function getStoredPreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as CookiePreferences;
  } catch {
    return null;
  }
}

export function getCookiePreferences(): CookiePreferences {
  return getStoredPreferences() ?? DEFAULT_PREFERENCES;
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = getStoredPreferences();
    if (!stored) {
      setVisible(true);
    } else {
      setPreferences(stored);
    }
  }, []);

  const savePreferences = useCallback((prefs: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setVisible(false);
    setShowSettings(false);
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences({ necessary: true, analytics: true, functional: true });
  }, [savePreferences]);

  const acceptNecessary = useCallback(() => {
    savePreferences({ necessary: true, analytics: false, functional: false });
  }, [savePreferences]);

  const saveCustom = useCallback(() => {
    savePreferences({ ...preferences, necessary: true });
  }, [preferences, savePreferences]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
      role="dialog"
      aria-label={t('cookieConsent.title')}
    >
      <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
              {t('cookieConsent.title')}
            </h2>
          </div>

          {/* Description */}
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
            {t('cookieConsent.description')}{' '}
            <Link
              to="/polityka-prywatnosci"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t('cookieConsent.privacyLink')}
            </Link>
          </p>

          {/* Settings panel */}
          {showSettings && (
            <div className="mb-4 space-y-3 border-t border-slate-200 dark:border-slate-700 pt-4">
              {/* Necessary */}
              <label className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('cookieConsent.categories.necessary.title')}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('cookieConsent.categories.necessary.description')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 cursor-not-allowed opacity-60"
                />
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('cookieConsent.categories.analytics.title')}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('cookieConsent.categories.analytics.description')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </label>

              {/* Functional */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('cookieConsent.categories.functional.title')}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('cookieConsent.categories.functional.description')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) => setPreferences(p => ({ ...p, functional: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </label>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={acceptAll}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
            >
              {t('cookieConsent.acceptAll')}
            </button>
            <button
              onClick={acceptNecessary}
              className="flex-1 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
            >
              {t('cookieConsent.acceptNecessary')}
            </button>
            {showSettings ? (
              <button
                onClick={saveCustom}
                className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
              >
                {t('cookieConsent.saveSettings')}
              </button>
            ) : (
              <button
                onClick={() => setShowSettings(true)}
                className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
              >
                {t('cookieConsent.customize')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
